import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { rupees, formatIndian, purityColor, formatApiError, formatWeight } from "@/lib/utils";
import { Search, Gem, Lock, User, Grid2x2, List, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProductDetailDrawer from "@/components/ProductDetailDrawer";
import PhotoPlaceholder from "@/components/PhotoPlaceholder";
import MemoBadge from "@/components/MemoBadge";
import { PublicDeleteDialog, deletePinErrorMessage } from "@/components/DeletePinControls";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared public catalogue page used for /distributor/:token and /retailer/:token.
 * Adapted from FranchiseView with a `kind` prop so a single component handles
 * both public link types. The franchise route uses FranchiseView directly.
 *
 * kind: 'distributor' | 'retailer'
 *   - 'distributor': owner view (PIN-gated) shows Distributor Cost column. Phase 1
 *     reuses the franchise pricing tier internally for distributor cost.
 *   - 'retailer': owner view (PIN-gated) shows the same columns as staff view for now;
 *     Phase 2 will add a Retailer Cost column. We display a small banner about that.
 */
export default function PublicCatalogueView({ kind }) {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [entity, setEntity] = useState(null);
  const [items, setItems] = useState(null);
  const [view, setView] = useState(params.get("view") === "owner" ? "owner" : "staff");
  const [pin, setPin] = useState(() => { localStorage.removeItem(`${kind}_pin_${token}`); return localStorage.getItem(`${kind}_sess_${token}`) || ""; });
  const [showPin, setShowPin] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  // CS 21.28 -- Multi-select for bulk share (PDF/Excel download).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  // CS 21.86 -- owner-unlocked distributor-link bulk delete.
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  const [shareFormat, setShareFormat] = useState("pdf");
  const [shareWithPrice, setShareWithPrice] = useState(true);
  // CS 21.29 -- tier selection when with_price=true. Server intersects with allowlist.
  // CS 21.46 -- Distributor OWNER additionally gets "Distributor Cost" (franchise
  // tier internally) as a checkbox in the bulk-share dialog. The owner uses
  // this to download their own selection with cost to send back to HO. The
  // server hard-gates this per `_card_allowed(channel, role)` -- team can never
  // include it even if they hand-craft the request.
  const availableTiers = useMemo(() => {
    if (kind === "distributor") {
      if (view === "owner") {
        return [
          { key: "franchise", label: "Distributor Cost" },
          { key: "retailer", label: "Retailer Price" },
          { key: "customer", label: "Customer MRP" },
        ];
      }
      return [
        { key: "retailer", label: "Retailer Price" },
        { key: "customer", label: "Customer MRP" },
      ];
    }
    return [{ key: "customer", label: "Customer MRP" }];
  }, [kind, view]);
  const [selectedTiers, setSelectedTiers] = useState(() => new Set(availableTiers.map((t) => t.key)));
  // When view flips owner <-> team, resync the selection so a stale
  // "franchise" pick from a previous owner session doesn't linger.
  useEffect(() => {
    setSelectedTiers((prev) => {
      const nextKeys = availableTiers.map((t) => t.key);
      const next = new Set();
      for (const k of nextKeys) if (prev.has(k)) next.add(k);
      // If nothing overlaps (e.g. franchise-only pick just got revoked),
      // default to everything currently available.
      if (next.size === 0) for (const k of nextKeys) next.add(k);
      return next;
    });
  }, [availableTiers]);
  const toggleTier = (k) => setSelectedTiers((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const [shareBusy, setShareBusy] = useState(false);
  // CS 21.72 -- Selection is uncapped. The cap is a SHARE-time constraint
  // that varies by format (PDF: 10, XLSX: uncapped) and is enforced both on
  // the client (block the download + friendly message) and on the server
  // (413 with a structured payload).
  const PDF_SHARE_CAP = 10;
  const bulkSupported = kind === "distributor" || kind === "retailer";
  // CS 21.86 -- deletion UI only on the distributor link's genuine owner view.
  const canDelete = kind === "distributor" && view === "owner" && !!pin;
  const toggleSelect = (jc) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(jc)) { next.delete(jc); return next; }
    next.add(jc);
    return next;
  });
  const selectAllVisible = () => setSelectedIds((prev) => {
    const next = new Set(prev);
    // CS 21.72 -- Select the entire filtered set; no client-side cap here.
    // Server-side share caps kick in only at download time (per-format).
    for (const it of sortedItems) next.add(it.product.jewel_code);
    return next;
  });
  const clearSelected = () => setSelectedIds(new Set());
  const doBulkShare = async () => {
    if (selectedIds.size === 0) return;
    // Client-side pre-flight for the PDF cap so the user gets an immediate
    // friendly message instead of waiting for the server 413.
    if (shareFormat === "pdf" && selectedIds.size > PDF_SHARE_CAP) {
      toast.error(`PDF is limited to ${PDF_SHARE_CAP} pieces; narrow selection or use Excel`);
      return;
    }
    setShareBusy(true);
    try {
      const { downloadBulkShare } = await import("@/lib/shareCard");
      const apiBase = process.env.REACT_APP_BACKEND_URL
        ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
      await downloadBulkShare({
        apiBase, tokenKind: kind, token,
        jewelCodes: Array.from(selectedIds),
        format: shareFormat, withPrice: shareWithPrice,
        selectedTiers: shareWithPrice ? Array.from(selectedTiers) : null,
        view, pin,
      });
      toast.success(`Downloaded ${selectedIds.size} pieces (${shareFormat.toUpperCase()})`);
      setShareDialogOpen(false);
    } catch (e) {
      toast.error(`Share failed: ${e.message}`);
    } finally {
      setShareBusy(false);
    }
  };
  // CS 21.86 -- bulk delete from the distributor owner link. Fresh delete PIN
  // authorises this one validated batch only; server enforces scope + lockout.
  const doBulkDelete = async (pinValue) => {
    setBulkDeleteBusy(true);
    try {
      const { data } = await axios.post(`${PUBLIC_API}/public/${kind}/${token}/products/delete`, {
        jewel_codes: Array.from(selectedIds),
        pin: pinValue,
      });
      const deleted = (data.deleted || []).length;
      const rejected = (data.rejected_out_of_scope || []).length;
      toast.success(`Deleted ${deleted} piece${deleted === 1 ? "" : "s"}${rejected ? ` · ${rejected} out-of-scope skipped` : ""}`);
      setBulkDeleteOpen(false);
      clearSelected();
      loadProducts(search);
    } catch (e) {
      toast.error(deletePinErrorMessage(e));
    } finally {
      setBulkDeleteBusy(false);
    }
  };
  const [rateMeta, setRateMeta] = useState(null);
  const [memoFilter, setMemoFilter] = useState("all");
  // CS 21.48 -- Category filter (distributor + retailer-under-distributor).
  // Populated from server-side distinct call so the dropdown lists exactly
  // the categories present in this link's stock. No hard-coding.
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const LAYOUT_KEY = `${kind}_layout_${token}`;
  const [layout, setLayout] = useState(() => localStorage.getItem(LAYOUT_KEY) || "grid");
  useEffect(() => { localStorage.setItem(LAYOUT_KEY, layout); }, [layout, LAYOUT_KEY]);
  const [sort, setSort] = useState(null);

  const labels = useMemo(() => {
    if (kind === "distributor") {
      return {
        ownerLabel: "Owner View",
        teamLabel: "Team View",
        ownerBtnLabel: "Owner View",
        teamBtnLabel: "Team View",
        sectionLabel: "Welcome",
      };
    }
    return {
      ownerLabel: "Owner View",
      teamLabel: "Staff View",
      ownerBtnLabel: "Owner View",
      teamBtnLabel: "Staff View",
      sectionLabel: "Retailer Catalogue",
    };
  }, [kind]);

  // CS 21.23 -- Auto-detect visible cost tier columns from server response.
  // Server sends franchise (Distributor Cost) only on distributor_owner; retailer
  // (Retailer Cost) on distributor_owner/distributor_team/retailer_owner; customer always.
  // Stripped tiers are ABSENT from JSON, not just hidden -- so this drives visibility.
  const costTiers = useMemo(() => {
    if (!items || items.length === 0) return [];
    const first = items[0].pricing || {};
    const tiers = [];
    if (first.franchise) tiers.push({ key: "franchise", label: "Distributor Cost" });
    if (first.retailer) tiers.push({ key: "retailer", label: "Retailer Cost" });
    return tiers;
  }, [items]);

  const cycleSort = (key) => {
    setSort((s) => {
      if (!s || s.key !== key) return { key, dir: "asc" };
      if (s.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const sortedItems = useMemo(() => {
    if (!items || !sort) return items;
    const getVal = (it) => {
      switch (sort.key) {
        case "net_weight": return Number(it.product.net_weight) || 0;
        case "dia_wt": return Number(it.pricing?.total_diamond_ct) || 0;
        case "franchise": return Number(it.pricing?.franchise?.total) || 0;
        case "retailer": return Number(it.pricing?.retailer?.total) || 0;
        case "customer": return Number(it.pricing?.customer?.total) || 0;
        default: return 0;
      }
    };
    const arr = [...items];
    arr.sort((a, b) => sort.dir === "asc" ? getVal(a) - getVal(b) : getVal(b) - getVal(a));
    return arr;
  }, [items, sort]);

  useEffect(() => {
    axios.get(`${PUBLIC_API}/public/${kind}/${token}`)
      .then((r) => setEntity(r.data))
      .catch(() => { toast.error("Invalid or inactive link"); setEntity(false); });
    axios.get(`${PUBLIC_API}/rates/public`).then((r) => setRateMeta(r.data)).catch(() => {});
  }, [token, kind]);

  const loadProducts = async (q = "") => {
    setItems(null);
    try {
      const qs = new URLSearchParams({ view });
      if (view === "owner" && pin) qs.set("pin", pin);
      if (q) qs.set("search", q);
      if (memoFilter === "memo") qs.set("is_memo", "true");
      if (memoFilter === "sold") qs.set("is_memo", "false");
      // CS 21.48 -- server-side category filter. "all" -> no param.
      if (categoryFilter && categoryFilter !== "all") qs.set("category", categoryFilter);
      const { data } = await axios.get(`${PUBLIC_API}/public/${kind}/${token}/products?${qs}`);
      setItems(data.items);
    } catch (e) {
      if (view === "owner" && e.response?.status === 401) {
        setView("staff");
        setPin("");
        localStorage.removeItem(`${kind}_sess_${token}`);
        toast.error("PIN incorrect");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
      }
    }
  };

  useEffect(() => {
    if (entity) loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, view, pin, memoFilter, categoryFilter]);

  // CS 21.48 -- Fetch the distinct category list once the entity resolves
  // (and refresh on view/pin change so owner-only categories can surface,
  // though today the endpoint returns the same list regardless of role).
  useEffect(() => {
    if (!entity) return;
    if (kind !== "distributor" && kind !== "retailer") return;
    const qs = new URLSearchParams({ view });
    if (view === "owner" && pin) qs.set("pin", pin);
    axios
      .get(`${PUBLIC_API}/public/${kind}/${token}/categories?${qs}`)
      .then((r) => setCategoryOptions(r.data?.categories || []))
      .catch(() => setCategoryOptions([]));
  }, [entity, kind, token, view, pin]);

  useEffect(() => {
    if (!entity) return;
    const t = setTimeout(() => loadProducts(search), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const submitPin = async () => {
    try {
      const { data } = await axios.post(`${PUBLIC_API}/public/${kind}/${token}/verify-pin`, { pin: pinInput });
      setPin(data.session);
      localStorage.setItem(`${kind}_sess_${token}`, data.session);
      setView("owner");
      setParams({ view: "owner" });
      setShowPin(false);
      setPinInput("");
      toast.success("Owner view unlocked");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Incorrect PIN");
    }
  };

  const switchToStaff = () => {
    setView("staff");
    setPin("");
    localStorage.removeItem(`${kind}_sess_${token}`);
    setParams({});
  };

  if (entity === null) return <div className="min-h-screen flex items-center justify-center"><div className="skeleton h-6 w-40" /></div>;
  if (entity === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F3] p-8">
        <div className="text-center">
          <Lock className="w-12 h-12 mx-auto text-[#C9A84C] mb-4" strokeWidth={1} />
          <h1 className="font-serif text-3xl text-[#0D1B2A]">Link unavailable</h1>
          <p className="text-slate-500 mt-2">This share link is invalid or has been deactivated.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid={`${kind}-view`}>
      <header className="glass-nav sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid={`${kind}-brand`}>
            <img
              src="/brand/ashth_logo.png"
              alt="Ashth"
              className="h-14 w-14 object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = "inline"; }}
            />
            <span className="font-serif text-3xl tracking-[0.3em] text-[#0D1B2A]" style={{ display: "none" }}>APS</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-wider text-slate-500 hidden sm:inline">{view === "owner" ? labels.ownerLabel : labels.teamLabel}</span>
            {view === "staff" ? (
              <Button onClick={() => setShowPin(true)} variant="outline" className="border-[#0D1B2A]/15" data-testid="open-owner-btn">
                <Lock className="w-4 h-4 mr-2" /> {labels.ownerBtnLabel}
              </Button>
            ) : (
              <Button onClick={switchToStaff} variant="outline" className="border-[#0D1B2A]/15" data-testid="switch-staff-btn">
                <User className="w-4 h-4 mr-2" /> {labels.teamBtnLabel}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C9A84C] mb-3">{labels.sectionLabel}</p>
          <h1 className="font-serif text-4xl md:text-6xl text-[#0D1B2A]" data-testid={`${kind}-name`}>{entity.name}</h1>
          {entity.city && <p className="text-slate-500 mt-2">{entity.city}</p>}
        </div>

        {kind === "retailer" && view === "staff" && costTiers.length === 0 && (
          <div className="luxe-card p-4 mb-8 bg-amber-50/50 border-l-4 border-amber-300 max-w-3xl mx-auto" data-testid="retailer-tier-notice">
            <p className="text-sm text-amber-800">
              Switch to <strong>Owner View</strong> with PIN to see Retailer Cost. Staff view shows Customer MRP only.
            </p>
          </div>
        )}

        <div className="luxe-card p-2 max-w-2xl mx-auto flex items-center gap-2 mb-12" data-testid={`${kind}-search-bar`}>
          <Search className="w-5 h-5 text-slate-400 ml-3" strokeWidth={1.5} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Jewel Code or Style…"
            className="border-0 shadow-none focus-visible:ring-0 h-12 text-base bg-transparent"
            data-testid={`${kind}-search-input`}
          />
        </div>

        <div className="gold-divider mb-10" />

        <div className="flex flex-wrap items-center justify-between mb-6 gap-3">
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">
            {items === null ? "Loading…" : `${items.length} product${items.length === 1 ? "" : "s"}`}
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-9 bg-white border-[#0D1B2A]/15 min-w-0 flex-1 sm:flex-none sm:min-w-[160px]" data-testid={`${kind}-category-filter`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid={`${kind}-category-opt-all`}>All Categories</SelectItem>
                {categoryOptions.map((c) => (
                  <SelectItem key={c.key} value={c.key} data-testid={`${kind}-category-opt-${c.key}`}>
                    {c.label}{typeof c.count === "number" ? ` (${c.count})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={memoFilter} onValueChange={setMemoFilter}>
              <SelectTrigger className="h-9 bg-white border-[#0D1B2A]/15 min-w-0 flex-1 sm:flex-none sm:min-w-[140px]" data-testid={`${kind}-status-filter`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="memo">Memo only</SelectItem>
                <SelectItem value="sold">Sold only</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={() => setLayout("grid")} data-testid={`${kind}-view-grid`}
              className={`p-2.5 rounded-md border ${layout === "grid" ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "border-[#0D1B2A]/15 text-slate-500 bg-white"}`}>
              <Grid2x2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button onClick={() => setLayout("list")} data-testid={`${kind}-view-list`}
              className={`p-2.5 rounded-md border ${layout === "list" ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "border-[#0D1B2A]/15 text-slate-500 bg-white"}`}>
              <List className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {items === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => <div key={`skel-${i}`} className="skeleton h-72" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="luxe-card p-16 text-center">
            <Gem className="w-10 h-10 mx-auto text-[#C9A84C] mb-3" strokeWidth={1} />
            <h3 className="font-serif text-2xl text-[#0D1B2A]">No products yet</h3>
            <p className="text-slate-500 text-sm">Your catalogue will appear here once products are uploaded.</p>
          </div>
        ) : layout === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sortedItems.map((it) => (
              <PublicCard key={it.product.jewel_code} item={it} costTiers={costTiers}
                selectable={bulkSupported}
                selected={selectedIds.has(it.product.jewel_code)}
                disabled={false}
                onToggle={() => toggleSelect(it.product.jewel_code)}
                onClick={() => setSelected(it.product.jewel_code)} />
            ))}
          </div>
        ) : (
          <div className="luxe-card overflow-hidden" data-testid={`${kind}-list`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0D1B2A]/3 text-left">
                  <tr className="text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-6 py-4">Jewel Code</th>
                    <th className="px-6 py-4">Style</th>
                    <th className="px-6 py-4 hidden md:table-cell">Category</th>
                    <th className="px-6 py-4">Purity</th>
                    <PublicSortableTh label="Net Wt" sortKey="net_weight" sort={sort} onSort={cycleSort} />
                    <PublicSortableTh label="Dia Wt" sortKey="dia_wt" sort={sort} onSort={cycleSort} />
                    {costTiers.map((t) => (
                      <PublicSortableTh key={t.key} label={t.label} sortKey={t.key} sort={sort} onSort={cycleSort} />
                    ))}
                    <PublicSortableTh label="Customer MRP" sortKey="customer" sort={sort} onSort={cycleSort} accent />
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((it) => (
                    <tr key={it.product.jewel_code}
                      onClick={() => setSelected(it.product.jewel_code)}
                      data-testid={`${kind}-list-row-${it.product.jewel_code}`}
                      className="border-t border-[#0D1B2A]/5 hover:bg-[#FAF8F3] cursor-pointer">
                      <td className="px-6 py-4 font-medium text-[#0D1B2A] whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          <span>{it.product.jewel_code}</span>
                          {it.product.is_memo && <MemoBadge />}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{it.product.style_no}</td>
                      <td className="px-6 py-4 text-slate-600 hidden md:table-cell">{it.product.category}</td>
                      <td className="px-6 py-4"><span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${purityColor(it.product.purity)}`}>{it.product.purity}</span></td>
                      <td className="px-6 py-4 text-right tabular-nums">{formatWeight(it.product.net_weight)} g</td>
                      <td className="px-6 py-4 text-right tabular-nums text-slate-600">{formatIndian(it.pricing?.total_diamond_ct || 0, { decimals: 3 })} ct</td>
                      {costTiers.map((t) => (
                        <td key={t.key} className="px-6 py-4 text-right tabular-nums text-slate-700">{it.pricing[t.key] ? rupees(it.pricing[t.key].total) : "—"}</td>
                      ))}
                      <td className="px-6 py-4 text-right tabular-nums font-medium text-[#0D1B2A] border-l-2 border-[#C9A84C]/40 bg-[#C9A84C]/5">{rupees(it.pricing.customer.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {rateMeta && (
          <p className="text-xs text-center text-slate-400 mt-16">
            Prices valid as of {new Date(rateMeta.last_updated_at || Date.now()).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} · Gold rate {rupees(rateMeta.gold_rate)}/g
          </p>
        )}
      </main>

      {/* CS 21.28 -- Sticky bulk-share action bar (visible when any selected) */}
      {bulkSupported && selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-[#0D1B2A]/10 shadow-lg" data-testid="bulk-share-bar">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 justify-between">
            <div className="text-sm text-slate-700">
              {/* CS 21.72 -- Uncapped selection. Report the TRUE count.
                  Per-format share caps are enforced at download time. */}
              <span className="font-medium" data-testid="bulk-selected-count">{selectedIds.size}</span>{" "}
              piece{selectedIds.size === 1 ? "" : "s"} selected
              {shareFormat === "pdf" && selectedIds.size > PDF_SHARE_CAP && (
                <span className="text-amber-700 ml-2" data-testid="pdf-cap-hint">
                  (PDF cap {PDF_SHARE_CAP} — switch to Excel to keep all {selectedIds.size})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={selectAllVisible} className="text-xs text-slate-500 hover:text-[#0D1B2A]" data-testid="select-all-btn">
                Select all visible
              </button>
              <button onClick={clearSelected} className="text-xs text-slate-500 hover:text-[#0D1B2A]" data-testid="clear-selected-btn">
                Clear
              </button>
              {canDelete && (
                <Button
                  onClick={() => setBulkDeleteOpen(true)}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="open-bulk-delete-btn"
                >
                  Delete {selectedIds.size}
                </Button>
              )}
              <Button
                onClick={() => setShareDialogOpen(true)}
                className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white"
                data-testid="open-share-dialog-btn"
              >
                Share Card
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Share dialog: format + with/without prices */}
      {bulkSupported && (
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent className="bg-white max-w-md" data-testid="share-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Share {selectedIds.size} piece{selectedIds.size === 1 ? "" : "s"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-2">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">With prices?</div>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button"
                    onClick={() => setShareWithPrice(true)}
                    data-testid="share-with-price"
                    className={`h-11 rounded-md border text-sm ${shareWithPrice ? "border-[#0D1B2A] bg-[#0D1B2A] text-white" : "border-[#0D1B2A]/15 bg-white text-slate-600 hover:bg-[#FAF8F3]"}`}
                  >With prices</button>
                  <button type="button"
                    onClick={() => setShareWithPrice(false)}
                    data-testid="share-without-price"
                    className={`h-11 rounded-md border text-sm ${!shareWithPrice ? "border-[#0D1B2A] bg-[#0D1B2A] text-white" : "border-[#0D1B2A]/15 bg-white text-slate-600 hover:bg-[#FAF8F3]"}`}
                  >Without prices</button>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">
                  {shareWithPrice
                    ? (kind === "distributor"
                        ? (view === "owner"
                            ? "Distributor Cost · Retailer Price · Customer MRP — pick any combination."
                            : "Retailer Price + MRP (Distributor Cost is owner-only).")
                        : "MRP only (Retailer Cost never shown to customer)")
                    : "Photo + specs only. No prices."}
                </p>
              </div>
              {shareWithPrice && availableTiers.length > 1 && (
                <div data-testid="share-tier-picker">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Which price tiers?</div>
                  <div className="space-y-2">
                    {availableTiers.map((t) => (
                      <label key={t.key} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedTiers.has(t.key)}
                          onChange={() => toggleTier(t.key)}
                          disabled={selectedTiers.size === 1 && selectedTiers.has(t.key)}
                          className="w-4 h-4 accent-[#C9A84C]"
                          data-testid={`share-tier-${t.key}`}
                        />
                        <span className="text-sm">{t.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Uncheck to hide a tier. At least one tier stays selected.
                  </p>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Format</div>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button"
                    onClick={() => setShareFormat("pdf")}
                    data-testid="share-format-pdf"
                    className={`h-11 rounded-md border text-sm ${shareFormat === "pdf" ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#0D1B2A]" : "border-[#0D1B2A]/15 bg-white text-slate-600 hover:bg-[#FAF8F3]"}`}
                  >PDF (one card per page)</button>
                  <button type="button"
                    onClick={() => setShareFormat("xlsx")}
                    data-testid="share-format-xlsx"
                    className={`h-11 rounded-md border text-sm ${shareFormat === "xlsx" ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#0D1B2A]" : "border-[#0D1B2A]/15 bg-white text-slate-600 hover:bg-[#FAF8F3]"}`}
                  >Excel (stock list)</button>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">
                  {shareFormat === "pdf"
                    ? "One PDF, one card per page. Forward via WhatsApp / email."
                    : "One Excel sheet, one row per piece with photo thumbnail."}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShareDialogOpen(false)} disabled={shareBusy}>Cancel</Button>
              <Button
                onClick={doBulkShare}
                disabled={shareBusy || selectedIds.size === 0}
                className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white"
                data-testid="share-generate-btn"
              >
                {shareBusy ? "Generating…" : `Download ${shareFormat.toUpperCase()}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={showPin} onOpenChange={setShowPin}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{labels.ownerBtnLabel} PIN</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Enter the 4-digit {kind === "retailer" ? "retailer" : "distributor"} PIN to unlock owner-level details.</p>
          <Input
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
            maxLength={4}
            inputMode="numeric"
            placeholder="0000"
            className="text-2xl tracking-[0.5em] text-center h-14"
            data-testid="owner-pin-input"
            onKeyDown={(e) => { if (e.key === "Enter") submitPin(); }}
          />
          <Button onClick={submitPin} disabled={pinInput.length !== 4} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="submit-owner-pin">Unlock</Button>
        </DialogContent>
      </Dialog>

      {selected && (
        <ProductDetailDrawer
          jewelCode={selected}
          token={token}
          tokenKind={kind}
          view={view}
          pin={view === "owner" ? pin : undefined}
          onDeleted={() => loadProducts(search)}
          onClose={() => setSelected(null)}        />
      )}

      <PublicDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        codes={Array.from(selectedIds)}
        busy={bulkDeleteBusy}
        onConfirm={doBulkDelete}
        testidPrefix="bulk-delete"
        scopeNote="Removes the selected pieces from this distributor's stock — they disappear from both the Distributor and Retailer links."
      />
    </div>
  );
}

function PublicSortableTh({ label, sortKey, sort, onSort, accent }) {
  const active = sort?.key === sortKey;
  const dir = active ? sort.dir : null;
  const accentCls = accent ? "border-l-2 border-[#C9A84C]/40 bg-[#C9A84C]/5" : "";
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`px-6 py-4 text-right cursor-pointer select-none hover:bg-[#0D1B2A]/5 transition-colors ${accentCls} ${active ? "text-[#C9A84C]" : ""}`}
    >
      <span className="inline-flex items-center gap-1 justify-end w-full">
        {label}
        {!active && <ChevronsUpDown className="w-3 h-3 text-slate-400" strokeWidth={2} />}
        {active && dir === "asc" && <ChevronUp className="w-3.5 h-3.5 text-[#C9A84C]" strokeWidth={2.5} />}
        {active && dir === "desc" && <ChevronDown className="w-3.5 h-3.5 text-[#C9A84C]" strokeWidth={2.5} />}
      </span>
    </th>
  );
}

function PublicCard({ item, costTiers, onClick, selectable, selected, disabled, onToggle }) {
  const p = item.product;
  const pr = item.pricing;
  return (
    <div className="luxe-card p-0 overflow-hidden group relative">
      {selectable && (
        <label
          className={`absolute top-3 right-3 z-10 flex items-center gap-2 px-2 py-1 rounded-full text-[11px] cursor-pointer select-none backdrop-blur-sm ${selected ? "bg-[#0D1B2A] text-white" : disabled ? "bg-white/70 text-slate-300" : "bg-white/85 text-slate-600 hover:bg-white"}`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`select-${p.jewel_code}`}
        >
          <input
            type="checkbox"
            className="w-4 h-4 accent-[#C9A84C]"
            checked={selected}
            disabled={disabled}
            onChange={onToggle}
          />
          {selected ? "Selected" : disabled ? "Max 10" : "Select"}
        </label>
      )}
      <button onClick={onClick} data-testid={`public-card-${p.jewel_code}`} className="w-full text-left p-0 block">
        <div className="aspect-square bg-[#FAF8F3] flex items-center justify-center overflow-hidden relative">
          {p.photo_url ? (
            <img src={p.photo_url} alt={p.jewel_code} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : <PhotoPlaceholder />}
          {p.is_memo && <div className="absolute top-3 left-3"><MemoBadge /></div>}
        </div>
        <div className="p-5 space-y-3">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-medium text-[#0D1B2A]">{p.jewel_code}</div>
              <div className="text-xs text-slate-500 mt-0.5">{p.style_no}</div>
            </div>
            <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${purityColor(p.purity)}`}>{p.purity}</span>
          </div>
          <div className="flex justify-between text-xs text-slate-500">
            <span>{p.category}</span>
            <span className="tabular-nums">{formatWeight(p.net_weight)} g</span>
          </div>
          {(pr.total_diamond_pcs > 0 || pr.total_diamond_ct > 0) && (
            <div className="flex justify-between text-[11px] text-slate-500 -mt-1" data-testid={`public-card-diamonds-${p.jewel_code}`}>
              <span className="uppercase tracking-wider text-[10px] text-slate-400">Diamonds</span>
              <span className="tabular-nums">
                {pr.total_diamond_pcs > 0 && `${formatIndian(pr.total_diamond_pcs)} pcs · `}
                {formatIndian(pr.total_diamond_ct, { decimals: 3 })} ct
              </span>
            </div>
          )}
          <div className="pt-3 border-t border-[#0D1B2A]/5">
            {costTiers && costTiers.map((t) => pr[t.key] ? (
              <div className="flex justify-between text-xs mb-1.5" key={t.key}>
                <span className="text-slate-500">{t.label}</span>
                <span className="tabular-nums text-slate-700">{rupees(pr[t.key].total)}</span>
              </div>
            ) : null)}
            <div className="flex justify-between items-baseline border-l-2 border-[#C9A84C] pl-2 -ml-2">
              <span className="text-[11px] uppercase tracking-wider text-[#C9A84C]">Customer MRP</span>
              <span className="font-serif text-xl text-[#0D1B2A] tabular-nums">{rupees(pr.customer.total)}</span>
            </div>
          </div>
        </div>
      </button>
    </div>
  );
}
